/* LITTLEG-MY-PROFILE-SUMMARY-02
 * My presentation over canonical LittleGProfile, LittleGFamilyData and LittleGResultStorage APIs.
 * Local profile writes must go through window.LittleGProfile only.
 * No direct storage-key access, backend call, public profile or badge fabrication.
 */
(function littleGMyProfileSummary(window, document) {
  'use strict';

  var VERSION = 'LITTLEG-MY-PROFILE-SUMMARY-02';
  var ROOT_SELECTOR = '[data-littleg-my-profile-summary]';
  var mounted = new Set();

  function clean(value) { return String(value == null ? '' : value).trim(); }
  function all(scope, selector) { return Array.prototype.slice.call((scope || document).querySelectorAll(selector)); }

  /**
   * @param {unknown} failure
   * @returns {string}
   */
  function failureMessage(failure) {
    if (
      failure !== null &&
      typeof failure === 'object' &&
      'message' in failure &&
      typeof failure.message === 'string'
    ) {
      return failure.message;
    }
    return '';
  }

  function profileOwner() {
    var owner = window.LittleGProfile;
    return owner && typeof owner.getIdentityState === 'function' ? owner : null;
  }

  function familyOwner() {
    var owner = window.LittleGFamilyData;
    return owner && typeof owner.listRecords === 'function' ? owner : null;
  }

  function resultOwner() {
    var owner = window.LittleGResultStorage;
    return owner && typeof owner.listAll === 'function' ? owner : null;
  }

  function ownersReady() {
    var profile = profileOwner();
    var family = familyOwner();
    var results = resultOwner();
    return Boolean(profile && family && typeof family.isFamilyRecord === 'function' && results);
  }

  function familyRecords() {
    var owner = familyOwner();
    if (!owner) return [];
    try {
      var value = owner.listRecords();
      return Array.isArray(value) ? value : [];
    } catch (_error) { return []; }
  }

  function cityCards() {
    var storage = resultOwner();
    var family = familyOwner();
    if (!storage || typeof storage.isCityCard !== 'function' || !family || typeof family.isFamilyRecord !== 'function') return [];
    try {
      var value = storage.listAll();
      if (!Array.isArray(value)) return [];
      return value.filter(function (entry) { return entry && !family.isFamilyRecord(entry) && storage.isCityCard(entry); });
    } catch (_error) { return []; }
  }

  function savedAt(record) {
    return clean(record && (record.saved_at || record.created_at || record.generated_at || record.confirmed_at || record.updated_at));
  }

  function dayKey(value) {
    var date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return '';
    return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');
  }

  function renderProfile(root) {
    var owner = profileOwner();
    if (!owner) return;
    if (typeof owner.reconcile === 'function') owner.reconcile();
    var profile = typeof owner.get === 'function' ? owner.get() : null;
    var sync = owner.livingCV && owner.livingCV.snapshot();
    root.setAttribute('data-cloud-sync', String(!!(sync && sync.authorized)));
    var capabilityStatus = root.querySelector('[data-cv-capability-status]');
    if (capabilityStatus) capabilityStatus.textContent = sync && sync.authorized
      ? (sync.available
          ? authCopy(root, 'Authorized · Private sync', '已授权 · 私人同步', '已授權 · 私人同步')
          : authCopy(root, 'Authorized · Connection unavailable', '已授权 · 当前无法连接', '已授權 · 目前無法連線'))
      : sync && sync.available
        ? authCopy(root, 'Available · Authorization required', '可使用 · 需要授权', '可使用 · 需要授權')
        : authCopy(root, 'Not available', '尚未开放', '尚未開放');
    var syncPanel = root.querySelector('[data-cv-sync-controls]');
    if (syncPanel) {
      syncPanel.hidden = !sync || !sync.owner || !sync.available;
      var syncStatus = root.querySelector('[data-cv-sync-status]');
      if (syncStatus) syncStatus.textContent = sync && sync.authorized
        ? (sync.sync_error ? authCopy(root, 'Private sync is temporarily unavailable. Your local edits are retained.', '私人同步暂时不可用，本机修改仍然保留。', '私人同步暫時無法使用，本機修改仍然保留。') : sync.pending ? 'Sync pending: ' + sync.pending : 'Private sync authorized') : 'Private sync is not authorized';
      var conflicts = root.querySelector('[data-cv-profile-conflicts]');
      if (conflicts) {
        conflicts.replaceChildren();
        (sync && sync.records || []).filter(function (record) {
          return ['profile', 'career'].indexOf(record.record_type) >= 0 && record.status === 'Conflict requires resolution';
        }).forEach(function (record) {
          var label = document.createElement('p'); label.textContent = record.record_type + ' · Conflict requires resolution'; conflicts.append(label);
          ['local', 'server'].forEach(function (choice) {
            var button = document.createElement('button'); button.type = 'button'; button.className = 'littleg-my-profile-summary__button';
            button.textContent = choice === 'local' ? 'Use local version (keep recovery copy)' : 'Use cloud version (keep recovery copy)';
            button.addEventListener('click', async function () { try { await owner.livingCV.resolve(record.record_id, choice); } catch (error) { if (syncStatus) syncStatus.textContent = error.message; } });
            conflicts.append(button);
          });
        });
      }
    }
    var session = window.LittleGWebSession;
    var webSignedIn = session && typeof session.getState === 'function' && session.getState().status === 'authenticated';
    var state = webSignedIn || profile ? 'littleg' : 'visitor';
    root.setAttribute('data-identity-state', state);
    var name = root.querySelector('[data-my-profile-name]');
    if (name) {
      var displayName = clean(profile && profile.display_name);
      name.textContent = displayName;
      name.hidden = !displayName;
    }

    var form = root.querySelector('[data-my-profile-form]');
    var formOwner = sync && sync.authorized && sync.owner || '';
    if (form && (form.dataset.cvOwner !== formOwner || !form.contains(document.activeElement))) {
      form.dataset.cvOwner = formOwner;
      var synchronizedRecords = sync && sync.records || [];
      form.dataset.cvProfileRevision = String(synchronizedRecords.find(function (r) { return r.record_type === 'profile'; })?.revision || 0);
      form.dataset.cvCareerRevision = String(synchronizedRecords.find(function (r) { return r.record_type === 'career'; })?.revision || 0);
      if (form.elements.display_name) form.elements.display_name.value = clean(profile && profile.display_name);
      if (form.elements.home_region) form.elements.home_region.value = clean(profile && profile.home_region);
      ['industry', 'company', 'career_role', 'about'].forEach(function (field) {
        if (form.elements[field]) form.elements[field].value = clean(profile && profile[field]);
      });
    }
  }

  function renderWebSession(root) {
    var node = root.querySelector('[data-my-web-session-status]');
    if (!node) return;
    var session = window.LittleGWebSession;
    var state = session && typeof session.getState === 'function'
      ? session.getState()
      : null;
    var status = state && state.status || 'booting';
    var locale = clean(root.getAttribute('data-locale')).toLowerCase();
    var english = locale.indexOf('en') === 0;
    var simplified = locale === 'zh-cn' || locale === 'zh-hans' || locale === 'zh-sg';
    var label;

      if (status === 'authenticated') {
      label = english ? 'LittleG account signed in' : (simplified ? 'LittleG 账户已登录' : 'LittleG 帳戶已登入');
    } else if (status === 'guest') {
      label = english
        ? 'LittleG account not signed in · this device can still use local data'
        : (simplified ? 'LittleG 账户未登录 · 本机资料仍可使用' : 'LittleG 帳戶未登入 · 本機資料仍可使用');
    } else if (status === 'error') {
      label = english
        ? 'Unable to verify LittleG account · local data is still available'
        : (simplified ? '暂时无法验证 LittleG 账户 · 本机资料仍可使用' : '暫時無法確認 LittleG 帳戶 · 本機資料仍可使用');
    } else {
      label = english
        ? 'Checking LittleG account status'
        : (simplified ? '正在检查 LittleG 账户状态' : '正在檢查 LittleG 帳戶狀態');
    }

    node.setAttribute('data-web-session-state', status);
    node.textContent = label;
    authControls(root, status);
  }

  function authCopy(root, en, cn, hk) {
    var locale = clean(root.getAttribute('data-locale')).toLowerCase();
    return locale.indexOf('en') === 0 ? en : (locale === 'zh-cn' || locale === 'zh-hans' || locale === 'zh-sg' ? cn : hk);
  }

  function authFeedback(root, message) {
    var node = root.querySelector('[data-my-web-auth-feedback]');
    if (node) node.textContent = message;
  }

  function authControls(root, status) {
    var form = root.querySelector('[data-my-web-login-form]');
    var logout = root.querySelector('[data-my-web-logout]');
    if (form) {
      form.hidden = status !== 'guest' && status !== 'error';
      var submit = form.querySelector('[data-my-web-login-submit]');
      if (submit) submit.disabled = Boolean(root.__littleGAuthBusy);
    }
    if (logout) {
      logout.hidden = status !== 'authenticated';
      logout.disabled = Boolean(root.__littleGAuthBusy);
    }
  }

  function bindWebAccount(root) {
    var form = root.querySelector('[data-my-web-login-form]');
    var logout = root.querySelector('[data-my-web-logout]');
    if (!form || !logout || form.getAttribute('data-web-auth-bound') === VERSION) return;
    form.setAttribute('data-web-auth-bound', VERSION);

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      if (root.__littleGAuthBusy) return;
      var session = window.LittleGWebSession;
      if (!session || typeof session.requestMagicLink !== 'function') {
        authFeedback(root, authCopy(root, 'LittleG sign-in is unavailable.', 'LittleG 登录暂时不可用。', 'LittleG 登入暫時未能使用。'));
        return;
      }
      root.__littleGAuthBusy = true;
      authControls(root, 'guest');
      authFeedback(root, authCopy(root, 'Sending sign-in link…', '正在发送登录链接…', '正在傳送登入連結…'));
      try {
        await session.requestMagicLink(form.elements.email && form.elements.email.value);
        form.reset();
        authFeedback(root, authCopy(root, 'Check your email for the LittleG sign-in link.', '请查看邮箱中的 LittleG 登录链接。', '請查看電郵中的 LittleG 登入連結。'));
      } catch (error) {
        authFeedback(root, error !== null && typeof error === 'object' && 'code' in error && error.code === 'provider_not_configured'
          ? authCopy(root, 'LittleG email sign-in is not configured yet.', 'LittleG 邮件登录服务尚未配置。', 'LittleG 電郵登入服務尚未設定。')
          : authCopy(root, 'Could not send the link. Check your email and retry.', '发送失败，请检查邮箱后重试。', '傳送失敗，請檢查電郵後重試。'));
      } finally {
        root.__littleGAuthBusy = false;
        renderWebSession(root);
      }
    });

    logout.addEventListener('click', async function () {
      if (root.__littleGAuthBusy) return;
      var session = window.LittleGWebSession;
      if (!session || typeof session.logout !== 'function') return;
      root.__littleGAuthBusy = true;
      authControls(root, 'authenticated');
      try {
        await session.logout();
        authFeedback(root, authCopy(root, 'Signed out of LittleG.', '已退出 LittleG 账户。', '已登出 LittleG 帳戶。'));
      } catch (_) {
        authFeedback(root, authCopy(root, 'Sign-out could not be confirmed. Retry when online.', '未能确认退出，请联网后重试。', '未能確認登出，請連線後重試。'));
      } finally {
        root.__littleGAuthBusy = false;
        renderWebSession(root);
      }
    });
  }

  function render(root) {
    if (!root || !root.isConnected) return false;
    renderWebSession(root);
    if (!ownersReady()) {
      root.setAttribute('data-runtime-ready', 'false');
      root.setAttribute('data-runtime-state', 'loading');
      return false;
    }

    var family = familyRecords();
    var cards = cityCards();
    var days = new Set(cards.map(function (card) { return dayKey(savedAt(card)); }).filter(Boolean));
    renderProfile(root);

    root.setAttribute('data-city-card-count', String(cards.length));
    root.setAttribute('data-family-record-count', String(family.length));
    root.setAttribute('data-explore-day-count', String(days.size));
    root.setAttribute('data-runtime-ready', 'true');
    root.setAttribute('data-runtime-state', 'ready');
    return true;
  }

  function bindForm(root) {
    var form = root.querySelector('[data-my-profile-form]');
    if (!form || form.getAttribute('data-profile-form-bound') === VERSION) return;
    form.setAttribute('data-profile-form-bound', VERSION);

    form.addEventListener('submit', async function (event) {
      event.preventDefault();
      var owner = profileOwner();
      if (!owner || typeof owner.update !== 'function') return;
      var synced = owner.livingCV && owner.livingCV.snapshot().authorized;
      var status = root.querySelector('[data-my-profile-settings-status]');
      try {
      var saved = await (synced ? owner.updateSynchronized : owner.update)({
        display_name: clean(form.elements.display_name && form.elements.display_name.value),
        home_region: clean(form.elements.home_region && form.elements.home_region.value),
        industry: clean(form.elements.industry && form.elements.industry.value),
        company: clean(form.elements.company && form.elements.company.value),
        career_role: clean(form.elements.career_role && form.elements.career_role.value),
        about: clean(form.elements.about && form.elements.about.value)
      }, synced ? { profile: Number(form.dataset.cvProfileRevision || 0), career: Number(form.dataset.cvCareerRevision || 0) } : 'profile-settings');
      if (status) {
        status.textContent = synced ? 'Saved locally · Sync pending' : saved
          ? (clean(root.getAttribute('data-save-success')) || 'LittleG profile saved on this device.')
          : 'Unable to save this profile on this device.';
      }
      render(root);
      } catch (error) {
        if (status) status.textContent = error !== null && typeof error === 'object' && 'partialSave' in error && error.partialSave
          ? authCopy(root, 'Part of your changes were saved locally. Review your profile and retry the remaining changes.', '部分修改已保存在本机，请检查资料后重试其余修改。', '部分修改已儲存在本機，請檢查資料後重試其餘修改。')
          : error.message || 'Unable to save';
      }
    });
    root.querySelector('[data-cv-authorize]')?.addEventListener('click', async function () {
      var owner = profileOwner(), status = root.querySelector('[data-cv-sync-status]');
      try {
        if (!root.querySelector('[data-cv-notice-accepted]').checked) throw new Error('Please read and accept the private synchronization notice.');
        await owner.livingCV.authorize();
      } catch (error) { if (status) status.textContent = error.message; }
    });
    root.querySelector('[data-cv-import-profile]')?.addEventListener('click', async function () {
      var status = root.querySelector('[data-cv-sync-status]');
      try {
        if (!root.querySelector('[data-cv-import-accepted]').checked) throw new Error('Confirm import of your own historical profile first.');
        await profileOwner().importLocalProfile();
      } catch (error) { if (status) status.textContent = error.message; }
    });
    root.querySelector('[data-cv-revoke]')?.addEventListener('click', async function () {
      try { await profileOwner().livingCV.revoke(); }
      catch (error) { root.querySelector('[data-cv-sync-status]').textContent = error.message; }
    });
  }

  function schedule(root) {
    var attempts = 0;
    (function attempt() {
      attempts += 1;
      if (render(root)) return;
      if (attempts >= 120) {
        root.setAttribute('data-runtime-ready', 'false');
        root.setAttribute('data-runtime-state', 'unavailable');
        var status = root.querySelector('[data-my-profile-settings-status]');
        if (status) status.textContent = authCopy(root,
          'Profile data is temporarily unavailable. Reload to retry.',
          '个人档案暂时无法读取，请重新加载后重试。',
          '個人檔案暫時無法讀取，請重新載入後重試。');
        return;
      }
      window.setTimeout(attempt, 50);
    })();
  }

  function mount(root) {
    if (!root || mounted.has(root)) return;
    mounted.add(root);
    root.setAttribute('data-runtime-bound', VERSION);
    bindForm(root);
    bindWebAccount(root);
    schedule(root);
  }

  function refresh() { mounted.forEach(render); }

  [
    'littleg:profile-change',
    'littleg:cv-change',
    'littleg:family-record-saved',
    'littleg:city-mission:result-saved',
    'littleg:family-data-ready',
    'littleg:family-data-cleared',
    'littleg:web-session-change'
  ].forEach(function (name) {
    document.addEventListener(name, refresh);
  });

  window.addEventListener('pageshow', refresh);
  window.addEventListener('popstate', refresh);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event && event.target ? event.target : document);
  });

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    all(target, ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); }, { once: true });
  } else {
    boot(document);
  }
})(window, document);
